import { ChartLoadingMessage } from '@modules/charts/components/ChartLoadingMessage';
import { BarChartLabelWidthUnit } from '@modules/charts/types';
import { CompareAggregateFormulaValueBarChart } from '@modules/quantify/components/QuantifyChart/CompareAggregateFormulaValueBarChart';
import { CompareBarChartWrapper } from '@modules/quantify/components/QuantifyChart/CompareBarChartWrapper';
import { QuantifyAggregateFormulaValueBarChart } from '@modules/quantify/components/QuantifyChart/QuantifyAggregateFormulaValueBarChart';
import { QuantifyTrendChartProps } from '@modules/quantify/components/QuantifyChart/QuantifyTrendChart/QuantifyTrendChart';
import { QuantifyTableView } from '@modules/quantify/components/QuantifyTableView';
import {
  useGetQuantifyCompareQuery,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Box, Paper, SxProps, Theme } from '@mui/material';
import { QuantifyVisualisationType } from 'generated/graphql';
import { QuantifyBarChart } from './QuantifyBarChart';
import { QuantifyPieChart } from './QuantifyPieChart';
import { QuantifyTrendChart } from './QuantifyTrendChart';

export interface QuantifyChartProps {
  shouldShowTitle?: boolean;
  shouldShowToggleActions?: boolean;
  component?: {
    barChart?: {
      containerSxProps?: SxProps<Theme>;
      yAxisCharLength?: number;
      onResizeComplete?: (labelWidthPercentage: number) => void;
      defaultLabelWidth?: number;
      defaultLabelWidthUnits?: BarChartLabelWidthUnit;
      sxProps?: SxProps<Theme>;
      shouldTakeFullHeight?: boolean;
      shouldShowToggleGroup?: boolean;
      minHeight?: number | undefined;
    };
    trendChart?: QuantifyTrendChartProps & {
      containerSxProps?: SxProps<Theme>;
    };
    sentimentChart?: {
      containerSxProps?: SxProps<Theme>;
      shouldDisableAnimation?: boolean;
    };
    pieChart?: {
      containerSxProps?: SxProps<Theme>;
      shouldShowLegendsInNewRow?: boolean;
      shouldDisableAnimation?: boolean;
    };
    statsChart?: { sx?: SxProps<Theme>; shouldDisableAnimation?: boolean };
    compareBarChart?: {
      containerSxProps?: SxProps<Theme>;
      shouldDisableAnimation?: boolean;
      chartHeight?: number;
      barLabelSxProps?: SxProps<Theme>;
      onResizeComplete?: (labelWidthPercentage: number) => void;
      defaultLabelWidth?: number;
      defaultLabelWidthUnits?: BarChartLabelWidthUnit;
      sxProps?: SxProps<Theme>;
      shouldTakeFullHeight?: boolean;
    };
  };
  shouldDisableInteraction?: boolean;
  shouldShowTableView?: boolean;
  isQuantifyPageView?: boolean;
  shouldDisableAddToFilter?: boolean;
  shouldShowMergeWithSelectedReasonsAction?: boolean;
}

export function QuantifyChart(props: QuantifyChartProps) {
  const {
    shouldShowTitle = true,
    shouldShowToggleActions = true,
    component = {
      barChart: {},
      trendChart: {},
      sentimentChart: {},
      pieChart: {},
    },
    shouldDisableInteraction = false,
    shouldDisableAddToFilter = false,
    shouldShowTableView = false,
    isQuantifyPageView = false,
    shouldShowMergeWithSelectedReasonsAction = true,
  } = props;

  const visualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );
  const isChartQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isChartQueryInProgress,
  );
  const compareQuery = useGetQuantifyCompareQuery();
  const isSingleRecordQuery = useIsSingleRecordQuery();

  if (shouldShowTableView && !isSingleRecordQuery) {
    return (
      <QuantifyTableView
        shouldDisableInteraction={shouldDisableInteraction}
        shouldShowMergeWithSelectedReasonsAction={
          shouldShowMergeWithSelectedReasonsAction
        }
      />
    );
  }

  const renderChart = () => {
    switch (visualisationType) {
      case QuantifyVisualisationType.Bar:
        if (compareQuery) {
          if (isSingleRecordQuery) {
            return (
              <Box
                sx={{
                  height: '100%',
                  ...component.compareBarChart?.containerSxProps,
                }}
              >
                <CompareAggregateFormulaValueBarChart {...props} />
              </Box>
            );
          }

          return (
            <Box
              sx={{
                height: '100%',
                ...component.compareBarChart?.containerSxProps,
              }}
            >
              <CompareBarChartWrapper
                shouldShowToggleGroup={shouldShowToggleActions}
                shouldShowAddToFilter={
                  !(shouldDisableInteraction || shouldDisableAddToFilter)
                }
                shouldShowTitle={shouldShowTitle}
                shouldDisableInteraction={shouldDisableInteraction}
                chartHeight={component.compareBarChart?.chartHeight}
                barLabelSxProps={component.compareBarChart?.barLabelSxProps}
                shouldShowAddToFilterForBothCompareFields={isQuantifyPageView}
                shouldShowLargeView={isQuantifyPageView}
                onResizeComplete={component.compareBarChart?.onResizeComplete}
                defaultLabelWidth={component.compareBarChart?.defaultLabelWidth}
                defaultLabelWidthUnits={
                  component.barChart?.defaultLabelWidthUnits
                }
                sxProps={component.compareBarChart?.sxProps}
                shouldTakeFullHeight={
                  component.compareBarChart?.shouldTakeFullHeight
                }
              />
            </Box>
          );
        }

        if (isSingleRecordQuery) {
          return (
            <Box
              sx={{
                height: '100%',
                ...component.barChart?.containerSxProps,
              }}
            >
              <QuantifyAggregateFormulaValueBarChart {...props} />
            </Box>
          );
        }

        return (
          <Box
            sx={{
              height: '100%',
              ...component.barChart?.containerSxProps,
            }}
          >
            <QuantifyBarChart
              {...component.barChart}
              shouldShowToggleGroup={
                component.barChart?.shouldShowToggleGroup ??
                shouldShowToggleActions
              }
              shouldShowAddToFilter={
                !(shouldDisableInteraction || shouldDisableAddToFilter)
              }
              shouldShowTitle={shouldShowTitle}
              shouldDisableInteraction={shouldDisableInteraction}
            />
          </Box>
        );
      case QuantifyVisualisationType.Trend:
        if (isSingleRecordQuery) {
          return (
            <Box
              p={2}
              sx={{ height: '100%', ...component.trendChart?.containerSxProps }}
            >
              <QuantifyTrendChart
                {...component.trendChart}
                shouldDisableAnomaliesToggle={true}
                shouldDisableCategoryToggle={true}
                shouldShowAddToFilter={false}
                shouldShowTitle={false}
                shouldDisableInteraction={shouldDisableInteraction}
                shouldShowToggleGroup={shouldShowToggleActions}
                shouldShowLegends={false}
              />
            </Box>
          );
        }

        return (
          <Box
            p={2}
            sx={{ height: '100%', ...component.trendChart?.containerSxProps }}
          >
            <QuantifyTrendChart
              {...component.trendChart}
              shouldShowToggleGroup={shouldShowToggleActions}
              shouldShowAddToFilter={
                !(shouldDisableInteraction || shouldDisableAddToFilter)
              }
              shouldShowTitle={shouldShowTitle}
              shouldDisableInteraction={shouldDisableInteraction}
            />
          </Box>
        );
      case QuantifyVisualisationType.Pie:
        return (
          <Box sx={{ height: '100%', ...component.pieChart?.containerSxProps }}>
            <QuantifyPieChart
              shouldShowAddToFilter={
                !(shouldDisableInteraction || shouldDisableAddToFilter)
              }
              shouldDisableInteraction={shouldDisableInteraction}
              shouldShowLegendsInNewRow={
                component.pieChart?.shouldShowLegendsInNewRow
              }
              shouldDisableAnimation={
                component.pieChart?.shouldDisableAnimation
              }
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box height="100%" position="relative">
      {renderChart()}
      {isChartQueryInProgress && (
        <Paper
          elevation={0}
          sx={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 0,
          }}
        >
          <ChartLoadingMessage />
        </Paper>
      )}
    </Box>
  );
}
